'use client';

import { useEffect } from 'react';
import report, { ReportEvent } from '@/lib/report';
import { getBuildId, getNetworkInfo } from '@/lib/chunk-error-handler';

/**
 * ChunkErrorMonitor - Global script loading error monitor
 *
 * Captures script load failures that don't trigger the error boundary
 * Provides early warning and additional observability for chunk issues
 *
 * Usage: Add once to root layout.tsx
 */
export function ChunkErrorMonitor() {
  useEffect(() => {
    const handleScriptError = (event: ErrorEvent) => {
      // Check if error is from a script tag
      const target = event.target as HTMLElement;

      if (target?.tagName === 'SCRIPT') {
        const scriptSrc = (target as HTMLScriptElement).src;

        console.error('[ChunkErrorMonitor] Script load failure:', scriptSrc);

        // Report to PostHog
        report(ReportEvent.ERROR_BOUNDARY_TRIGGERED, {
          error_type: 'script_load_failure',
          error_message: event.message || 'Script failed to load',
          script_src: scriptSrc,
          pathname: window.location.pathname,
          href: window.location.href,
          build_id: getBuildId(),
          user_agent: navigator.userAgent,
          ...getNetworkInfo(),
        });
      }
    };

    // Use capture phase to catch errors early
    window.addEventListener('error', handleScriptError, true);

    return () => {
      window.removeEventListener('error', handleScriptError, true);
    };
  }, []);

  return null;
}
